import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';
import { Card, InternalGrid, Text, CheckBoldIcon } from '@paygreen/paygreen-ui';
import IconByName from '../../../utils/IconByName';
import { IsFrench } from '../../../utils/tools';
import { ErrorBoundary } from '../../../components';

const notCheckedStyle = css`
    .checked {
        transform-origin: top right;
        transform: scale(0);
        transition: all ${(props) => props.theme.transition.xs};
    }
`;

const checkedStyle = css`
    .checked {
        transform: scale(1);
        transition-timing-function: cubic-bezier(0.9, -0.6, 0, 2);
    }
`;

const CardWrapper = styled.div`
    cursor: pointer;

    ${(props) => (props.isSelected ? checkedStyle : notCheckedStyle)};

    & > * {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        &:hover,
        &:focus,
        &:active {
            background-color: ${(props) => props.theme.color.primary.light};
        }
    }
`;

const ProductCard = ({ i18n, product, isSelected, onClick }) => {
    const { attributeNameFrench, monetaryRatioNameFrench, value, unit } =
        product;

    const IntValue = Number(value);

    return (
        <ErrorBoundary>
            <CardWrapper isSelected={isSelected}>
                <Card
                    isShadowWab={!isSelected}
                    shadowSize={isSelected ? 'none' : 'sm'}
                    hasBackground={true}
                    radiusSize="sm"
                    blockWidth="lg"
                    colorPallet={isSelected ? 'theme' : 'wab'}
                    paddingLateral="xs"
                    paddingTop="xs"
                    paddingBottom="xs"
                    htmlTag="div"
                    onClick={onClick}
                >
                    <div>
                        <InternalGrid
                            displayType="grid"
                            gridTemplateColumns="1fr auto"
                            alignItems="flex-start"
                            justifyItems="start"
                        >
                            <Text
                                textSize="sm"
                                htmlTag="strong"
                                style={{ lineHeight: '1.2' }}
                            >
                                {!monetaryRatioNameFrench && attributeNameFrench
                                    ? product[IsFrench(i18n, 'baseName')] +
                                      ' - ' +
                                      product[IsFrench(i18n, 'attributeName')]
                                    : product[IsFrench(i18n, 'baseName')]}
                                {!!monetaryRatioNameFrench &&
                                    product[
                                        IsFrench(i18n, 'monetaryRatioName')
                                    ]}
                            </Text>

                            <CheckBoldIcon className="checked" iconSize="md" />
                        </InternalGrid>

                        <Text
                            textSize="xs"
                            htmlTag="em"
                            colorWab="grey50"
                            marginTop="xs"
                            style={{ lineHeight: '1.2' }}
                        >
                            {product[IsFrench(i18n, 'sourceCategoryName')] ||
                                product[
                                    IsFrench(i18n, 'monetaryRatioCategoryName')
                                ]}
                        </Text>
                    </div>

                    <InternalGrid
                        childrenMarginTop="sm"
                        justifyContent="flex-start"
                        alignItems="flex-end"
                    >
                        <IconByName
                            name="FactoryIcon"
                            colorPallet="wab"
                            colorWab="grey30"
                            marginRight="xs"
                            iconSize="sm"
                        />

                        <Text
                            textSize="xs"
                            colorPallet="wab"
                            colorWab="grey30"
                            htmlTag="strong"
                        >
                            {Number(IntValue.toFixed(2))}&nbsp;
                            {unit || 'kgCO2eq/€'}
                        </Text>
                    </InternalGrid>
                </Card>
            </CardWrapper>
        </ErrorBoundary>
    );
};

ProductCard.propTypes = {
    product: PropTypes.object.isRequired,
    i18n: PropTypes.object.isRequired,
    onClick: PropTypes.func.isRequired,
    isSelected: PropTypes.bool,
};

ProductCard.defaultProps = {
    isSelected: false,
};

export default withTranslation('common')(ProductCard);
